import React from 'react';

interface ConflictItem {
  Employee: string;
  FirstName?: string;
  LastName?: string;
  ProfileColor?: string;
  ExistingPolicy: string;
  Reason: string;
}

interface ConflictData {
  StartDate?: string;
  EndDate?: string;
  Conflicts?: ConflictItem[];
}

interface PolicyConflictModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: ConflictData | null;
}



const AVATAR_COLORS = [
    { bg: '#E6F1FB', color: '#185FA5' },
    { bg: '#EAF3DE', color: '#3B6D11' },
    { bg: '#FAEEDA', color: '#854F0B' },
    { bg: '#FCEBEB', color: '#A32D2D' },
    { bg: '#EEEDFE', color: '#534AB7' },
    { bg: '#FBEAF0', color: '#993556' },
]

const PolicyConflictModal: React.FC<PolicyConflictModalProps> = ({ isOpen, onClose, data }) => {
  if (!isOpen || !data) return null;

  const conflicts = data.Conflicts || [];
  const startDate = data.StartDate || 'N/A';
  const endDate = data.EndDate || 'N/A';

  
  const getInitials = (item: ConflictItem) => {
    if (item.FirstName && item.LastName) {
      return `${item.FirstName.charAt(0)}${item.LastName.charAt(0)}`.toUpperCase();
    }
    
    if (item.Employee) {
      const parts = item.Employee.trim().split(' ');
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
      }
      return parts[0].substring(0, 2).toUpperCase();
    }
    return 'NA';
  };

  
  const getAvatarStyle = (index: number, customColor?: string) => {
    if (customColor) {
      return { backgroundColor: customColor, color: '#ffffff' };
    }
    const selectedColor = AVATAR_COLORS[index % AVATAR_COLORS.length];
    return {
      backgroundColor: selectedColor.bg,
      color: selectedColor.color
    };
  };

  return (
    <div 
      className="modal show d-block" 
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1050 }} 
      tabIndex={-1}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div 
          className="modal-content border-0 shadow" 
          style={{ borderRadius: '12px', backgroundColor: '#ffffff', overflow: 'hidden' }}
        >
          
          
          <div className="modal-header border-0 pb-0 pt-5 px-6">
            <h5 className="modal-title fw-bold fs-4" style={{ color: '#181c32', fontFamily: 'Inter, sans-serif' }}>
              Cannot Activate Attendance Policy
            </h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose} 
              aria-label="Close"
              style={{ opacity: 0.6 }}
            ></button>
          </div>

          
          <div className="modal-body p-6">
            
            <p className="fs-7 mb-4" style={{ color: '#5e6278', lineHeight: '1.6' }}>
              This policy cannot be activated because{' '}
              <span 
                className="fw-bold mx-1" 
                style={{ 
                  backgroundColor: '#f1f5f9', 
                  color: '#181c32', 
                  fontSize: '13px', 
                  padding: '4px 8px',
                  borderRadius: '6px',
                  display: 'inline-block'
                }}
              >
                {conflicts.length} employee(s)
              </span>{' '}
              are already covered by another active Attendance Policy during the selected effective period{' '}
              <strong style={{ color: '#181c32' }}>({startDate} - {endDate})</strong>.
            </p>

            
            <div 
              className="table-responsive mb-5 border rounded-2" 
              style={{ 
                maxHeight: '240px', 
                overflowY: 'auto', 
                borderColor: '#eff2f5',
                position: 'relative'
              }}
            >
              <table className="table table-row-dashed align-middle mb-0" style={{ borderColor: '#eff2f5' }}>
                
                
                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr className="fw-semibold border-bottom border-gray-200" style={{ fontSize: '13px' }}>
                    <th 
                      style={{ 
                        width: '35%', 
                        color: '#3f4254', 
                        backgroundColor: '#f9fafb',
                        boxShadow: 'inset 0 -1px 0 #eff2f5'
                      }} 
                      className="ps-4 py-3"
                    >
                      Employee
                    </th>
                    <th 
                      style={{ 
                        width: '25%', 
                        color: '#3f4254', 
                        backgroundColor: '#f9fafb',
                        boxShadow: 'inset 0 -1px 0 #eff2f5'
                      }} 
                      className="py-3"
                    >
                      Existing Policy
                    </th>
                    <th 
                      style={{ 
                        width: '40%', 
                        color: '#3f4254', 
                        backgroundColor: '#f9fafb',
                        boxShadow: 'inset 0 -1px 0 #eff2f5'
                      }} 
                      className="pe-4 py-3"
                    >
                      Reason
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {conflicts.map((item, index) => {
                    const avatarStyle = getAvatarStyle(index, item.ProfileColor);

                    return (
                      <tr key={index} style={{ backgroundColor: '#ffffff' }}>
                        
                        
                        <td className="ps-4 py-2.5">
                          <div className="d-flex align-items-center">
                            <div className="flex-shrink-0 me-3">
                              <div 
                                className="fw-bold"
                                style={{ 
                                  backgroundColor: avatarStyle.backgroundColor,
                                  color: avatarStyle.color,
                                  width: '30px',
                                  height: '30px',
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '11px',
                                  letterSpacing: '-0.3px'
                                }}
                              >
                                {getInitials(item)}
                              </div>
                            </div>
                            <span 
                              className="fw-medium" 
                              style={{ color: '#181c32', fontSize: '13px' }}
                            >
                              {item.Employee || `${item.FirstName} ${item.LastName}`}
                            </span>
                          </div>
                        </td>

                        
                        <td className="py-2.5">
                          <span 
                            style={{ 
                              backgroundColor: '#e8f2ff', 
                              color: '#185FA5', 
                              fontWeight:500, 
                              padding: '3px 8px', 
                              borderRadius: '5px', 
                              fontSize: '12px',
                              display: 'inline-block'
                            }}
                          >
                            {item.ExistingPolicy}
                          </span>
                        </td>

                        
                        <td className="pe-4 py-2.5" style={{ color: '#5e6278', fontSize: '12.5px', lineHeight: '1.4' }}>
                          {item.Reason.includes('Department:') ? (
                            <>Covered through <strong style={{ color: '#181c32' }}>Department:</strong> {item.Reason.replace('Covered through Department:', '')}</>
                          ) : item.Reason.includes('Location:') ? (
                            <>Covered through <strong style={{ color: '#181c32' }}>Location:</strong> {item.Reason.replace('Covered through Location:', '')}</>
                          ) : (
                            item.Reason
                          )}
                        </td>

                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            
            <div className="pt-4 border-top" style={{ borderColor: '#eff2f5' }}>
              <h6 className="fw-bold fs-7 mb-1" style={{ color: '#181c32' }}>How to resolve</h6>
              <p className="fs-8 mb-2" style={{ color: '#7e8299' }}>To activate this policy, choose one of the following:</p>
              <ul className="fs-7 ps-4 mb-0 lh-lg" style={{ color: '#3f4254' }}>
                <li>Remove the conflicting employees from the existing policy.</li>
                <li>Archive the existing policy.</li>
                <li>Modify the effective dates of this policy.</li>
                <li>Change the assignment scope of this policy.</li>
              </ul>
            </div>
          </div>

          
          <div className="modal-footer border-0 pt-0 pe-6 pb-5">
            <button 
              type="button" 
               className="btn btn-sm btn-secondary fs-7 px-5 fw-medium"
              onClick={onClose}
           
            >
              Close
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PolicyConflictModal;